if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        this.submitButton.addEventListener('click', (evt) => {
          if (this.submitButton.getAttribute('aria-disabled') === 'true') {
            evt.preventDefault();
            if (evt.detail > 0) evt.stopImmediatePropagation();
          }
        }, true);

        this.submitButton.addEventListener('keydown', (evt) => {
          if (evt.key === 'Enter') {
            evt.preventDefault();
            this.form.requestSubmit(this.submitButton);
          }
        });

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.announceCartAddition(response);
            this.openCartDrawer();

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            const cartUpdatePromise = !this.error
              ? publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                })
              : Promise.resolve();
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
            return cartUpdatePromise;
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      announceCartAddition(item) {
        const title = item.product_title || item.title || '';
        const quantity = item.quantity || 1;
        const unitPrice = item.final_price ? (item.final_price / 100) : null;
        const totalPrice = unitPrice !== null ? unitPrice * quantity : null;
        const currencyCode = window.Shopify && window.Shopify.currency && window.Shopify.currency.active ? window.Shopify.currency.active : 'USD';
        let priceText = '';
        if (totalPrice !== null) {
          try {
            priceText = new Intl.NumberFormat(navigator.language || 'en', { style: 'currency', currency: currencyCode }).format(totalPrice);
          } catch (e) {
            priceText = currencyCode + totalPrice.toFixed(2);
          }
        }
        const variantOptions = item.variant_options || [];
        const variant = variantOptions.length > 0 ? variantOptions[0] : (item.variant_title || '');
        const size = variantOptions.length > 1 ? variantOptions[1] : '';
        let message = title ? title : '';
        if (variant) message += ', ' + variant;
        if (size) message += ', size ' + size;
        message += ', quantity ' + quantity;
        if (priceText) message += ', price ' + priceText;
        message += ', added to cart';

        const announcer = document.createElement('div');
        announcer.setAttribute('aria-live', 'assertive');
        announcer.setAttribute('role', 'alert');
        announcer.setAttribute('aria-atomic', 'true');
        announcer.style.cssText = 'position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden;';
        document.body.appendChild(announcer);
        setTimeout(() => { announcer.textContent = message; }, 100);
        setTimeout(() => { if (announcer.parentNode) document.body.removeChild(announcer); }, 3100);
      }
      
      // Fix for AMP cart drawer not opening when clicking "Add to cart" button after cache clean 
      openCartDrawer() {
        const open = () => {
          if (!window.AMP_API || typeof window.AMP_API.OPEN_CART !== 'function') return false;
          if (typeof window.openCart === 'function') window.openCart();
          else window.AMP_API.OPEN_CART();
          return true;
        };

        if (open()) return;

        const deadline = Date.now() + 5000;
        const poll = setInterval(() => {
          if (open() || Date.now() > deadline) clearInterval(poll);
        }, 100);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
